import React from 'react';
import { Trans, withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import stepIcon from 'src/public/img/illustration-exposures-done.svg';

import { AppState } from 'src/config/appState';
import * as navigationActions from 'src/modules/navigation/actions/navigationActions';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import { ProgramEnum, ProgramWeekEnum } from 'src/modules/programs/models/programs';
import * as S from 'src/modules/programs/stepComponents/styles';
import {
    fetchSeveralProgramSurveys,
    submitProgramSurvey,
} from 'src/modules/programs/surveys/actions/surveysActions';
import {
    GenericSurveyAnswer,
    GenericSurveyQuestion,
} from 'src/modules/programs/surveys/models/surveys';
import {
    getProgramSurveyAnswers,
    getProgramSurveyQuestions,
    isProgramSurveyAnswered,
} from 'src/modules/programs/surveys/selectors/surveysSelectors';

import { CBTProgramWeekTwoStep, CBTProgramWeekTwoSurvey } from '../../weekTwo/models/weekTwo';
import { GoalImportance } from '../components/GoalImportance';
import { CBTProgramWeekSevenSurvey } from '../models/weekSeven';

const surveyId = CBTProgramWeekSevenSurvey.TakingTheNextStep;
const goalSurveyId = CBTProgramWeekTwoSurvey.WhatAreYourGoals;

interface ActionsProps {
    fetchSeveralProgramSurveys: typeof fetchSeveralProgramSurveys;
    submitProgramSurvey: typeof submitProgramSurvey;
    redirectToUrl: typeof navigationActions.redirectToUrl;
}

interface StateProps {
    goalAnswers: GenericSurveyAnswer[] | undefined;
    questions: GenericSurveyQuestion[] | undefined;
    answers: GenericSurveyAnswer[] | undefined;
    isSurveyAnswered: boolean;
}

interface Props
    extends StepButtonsProps,
        StateProps,
        ActionsProps,
        StepButtonsProps,
        WithTranslation {
    programId: ProgramEnum;
}

// This component will need rewriting after enabling multiple goals in Week 2: What Are Your Goals step - right now it handles one survey, for one goal
class TakingTheNextStepContainer extends React.Component<Props> {
    public componentDidMount(): void {
        const { programId } = this.props;

        this.props.fetchSeveralProgramSurveys({
            programId,
            surveysIds: [surveyId, goalSurveyId],
        });
    }

    private handleSubmit = () => {
        const { programId, answers, goToNextStep } = this.props;

        if (!answers) {
            return;
        }

        this.props.submitProgramSurvey({
            programId,
            surveyId,
            answers,
        });

        goToNextStep();
    };

    public render(): JSX.Element {
        const {
            programId,
            redirectToUrl,
            goToPreviousStep,
            isSurveyAnswered,
            questions,
            goalAnswers,
            t,
        } = this.props;

        const goalsUrl = `/programs/${programId}/${ProgramWeekEnum.WeekTwo}/${CBTProgramWeekTwoStep.WhatAreYourGoalsStep}`;

        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn md={14}>
                        <S.StepHeading>{t('week-7.taking-the-next-step.title')}</S.StepHeading>
                        <S.StepDescription small>
                            {t('week-7.taking-the-next-step.description.1')}
                        </S.StepDescription>
                        <S.StepDescription small>
                            {t('week-7.taking-the-next-step.description.2')}
                        </S.StepDescription>
                        {questions && goalAnswers && (
                            <GoalImportance
                                programId={programId}
                                surveyId={surveyId}
                                goalAnswers={goalAnswers}
                                question={questions[0]}
                            />
                        )}
                        <S.StepDescription small>
                            <Trans i18nKey="week-7.taking-the-next-step.description.3">
                                You can manage and change your goals
                                <S.TextLink onClick={() => redirectToUrl(goalsUrl)}>
                                    here
                                </S.TextLink>
                            </Trans>
                        </S.StepDescription>
                    </S.ContentColumn>
                    <S.ContentColumn md={10}>
                        <S.StepIcon large iconUrl={stepIcon} />
                    </S.ContentColumn>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!isSurveyAnswered}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState, { programId }: Props): StateProps => ({
    goalAnswers: getProgramSurveyAnswers(state, programId, goalSurveyId),
    questions: getProgramSurveyQuestions(state, programId, surveyId),
    answers: getProgramSurveyAnswers(state, programId, surveyId),
    isSurveyAnswered: isProgramSurveyAnswered(state, programId, surveyId),
});

export const TakingTheNextStep = withTranslation('cbt')(
    connect(mapStateToProps, {
        fetchSeveralProgramSurveys,
        submitProgramSurvey,
        redirectToUrl: navigationActions.redirectToUrl,
    })(TakingTheNextStepContainer),
);
